"use client";

import React, { useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { staticFormsActions } from "../actions/staticForms";
import { useDictionary } from "../context/dictionaryProvider";
import { trackEvent } from "../lib/engageXClient";

const newsletterSchema = yup
  .object({
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Email is required")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Email must have a valid domain"),
  })
  .required();

const BlogNewsletterStrip = () => {
  const dict = useDictionary();
  const [response, setResponse] = useState({ type: "", text: "" });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(newsletterSchema),
  });

  const onSubmit = async (data) => {
    setResponse({ type: "", text: "" });

    try {
      const res = await staticFormsActions("newsLetterForm", data);

      if (res?.status) {
        setResponse({
          type: "success",
          text: "You have been subscribed for the newsletter.",
        });
        reset();

        trackEvent("newsletter_subscribed", { location: "blog" });
        return;
      }

      setResponse({
        type: "error",
        text: "Subscription failed. Please try again.",
      });
    } catch (error) {
      setResponse({
        type: "error",
        text: "Subscription failed. Please try again.",
      });
    }
  };

  return (
    <section className="overflow-hidden rounded-[18px] border border-[#e2b743] bg-[#ffd86a] shadow-[0_10px_30px_rgba(208,158,28,0.18)]">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-5 px-5 py-6 md:px-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.9fr)_auto] lg:items-center lg:gap-8"
      >
        <div className="max-w-[420px]">
          <h2 className="text-[24px] font-extrabold leading-tight text-[#111111]">
            {dict?.newsLetter?.title}
          </h2>
          <p className="mt-2 text-[14px] leading-6 text-[#3f3a20]">
            {dict?.newsLetter?.description}
          </p>
          {response.text ? (
            <p
              className={`mt-3 text-sm font-medium ${
                response.type === "success" ? "text-[#0b7a35]" : "text-[#a12622]"
              }`}
            >
              {response.text}
            </p>
          ) : null}
        </div>

        <div className="w-full">
          <label
            htmlFor="blog-newsletter-email"
            className="block text-[12px] font-semibold text-[#111111]"
          >
            {dict?.form?.fieldNames?.email || "Email"}
          </label>
          <input
            id="blog-newsletter-email"
            type="email"
            {...register("email")}
            placeholder={dict?.form?.placeholders?.typeHere || "Type here"}
            aria-invalid={errors.email ? "true" : "false"}
            className="mt-2 h-11 w-full border-0 border-b border-[#8c7220] bg-transparent px-0 text-sm text-[#111111] outline-none placeholder:text-[#6e6340] focus:border-[#111111]"
          />
          {errors.email ? (
            <p className="mt-2 text-xs font-medium text-[#a12622]">
              {errors.email.message}
            </p>
          ) : null}
        </div>

        <div className="lg:justify-self-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex min-h-[48px] min-w-[150px] items-center justify-center rounded-full bg-[#111111] px-8 text-sm font-semibold text-white transition hover:bg-[#262626] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? "Submitting..." : dict?.subscribe || "Subscribe"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default BlogNewsletterStrip;
